// =============================================================
// SOURCE UNIQUE DE VÉRITÉ — Toutes les couleurs de CycleGala
//
// ⚠️  RÈGLE ABSOLUE : aucune valeur hex ou rgba ne doit être
//     écrite en dur dans les fichiers pages/ ou components/.
//     Toute couleur doit provenir d'ici, soit via un import JS,
//     soit via une variable CSS var(--*) injectée par injectCSSColors().
//
// ─── Exports JS disponibles ──────────────────────────────────
//
//   PHASE          — 4 phases du cycle + couleur primaire du thème
//   PHASE_CONTRAST — couleurs de texte contrasté sur fond de phase
//   MENTAL         — 3 métriques de l'état mental + couleurs d'accentuation
//   SPORT          — FC, RPE, niveaux d'essoufflement, palette d'activités
//   UI             — neutrals structurels (gris, texte, états vides, axes SVG)
//   HOME           — couleurs propres au dashboard (anneau vide, axe donut…)
//   ARTICLE        — badge catégorie + 5 gradients de cartes articles/blog
//
//   CYCLE_PIE_COLORS   — mapping label → couleur pour le camembert Cycle
//   MENTAL_PIE_COLORS  — mapping label → couleur pour le camembert Mental
//   SPORT_PIE_COLORS   — mapping label → couleur pour le camembert Sport
//   PHASE_COLORS_MAP   — mapping clé de phase → couleur (utilisé par les graphiques)
//   PHASE_COLORS       — config complète par phase (main, pastel, label)
//   toPhaseKey()       — normalise une clé de phase vers la nomenclature interne
//   injectCSSColors()  — injecte les variables CSS dans :root (appeler 1× dans index.js)
//
// ─── Variables CSS injectées par injectCSSColors() ───────────
//
//   --phase-menstruations  --phase-folliculaire  --phase-ovulation  --phase-luteale
//   --phase-primary        --mental-primary      --sport-primary
//   --sport                --sport-rest
//   --sport-rpe-facile     --sport-rpe-intense
//   --mental-concentration --mental-productivite --mental-estime
//
// =============================================================


// ─── Phases du cycle ─────────────────────────────────────────
//
// Ces 4 couleurs servent à identifier chaque phase sur tous les
// graphiques, badges, pastilles de calendrier et cartes de stat.
// Elles sont aussi injectées en CSS via injectCSSColors().

export const PHASE = {
  primary:      '#E10C3C',  // rouge vif  — couleur principale du thème Cycle (nav, titres)
  regles:       '#E10C3C',  // rouge vif  — phase Menstruations
  folliculaire: '#00F9AE',  // teal vif   — phase Folliculaire
  ovulation:    '#FFAC32',  // ambre      — phase Ovulatoire
  luteale:      '#8117EB',  // violet     — phase Lutéale
};


// ─── Contraste texte sur fond de phase ───────────────────────
//
// Certaines couleurs de phase sont trop saturées pour afficher
// du texte de la même teinte. Ces valeurs fournissent une version
// plus sombre utilisable en couleur de texte sur ces fonds.

export const PHASE_CONTRAST = {
  folliculaireDark: '#2A9D6A',  // vert foncé — lisible sur fond folliculaire #00F9AE (Coach.js)
};


// ─── Indicateurs mentaux ─────────────────────────────────────
//
// Couleurs des 3 métriques du graphique "État mental" ainsi que
// des éléments d'accentuation de la section Mental (avatars,
// badges coach, camembert de répartition d'humeur).

export const MENTAL = {
  primary:       '#F93E6E',  // rose vif     — couleur principale du thème Mental (nav, titres)
  concentration: '#F93E6E',  // rose vif     — ligne Concentration dans le graphique
  productivite:  '#FF91AA',  // rose moyen   — ligne Productivité dans le graphique
  estime:        '#AEA5F4',  // violet pâle  — ligne Estime de soi dans le graphique
  accent:        '#9B8EF5',  // violet moyen — avatars, cercles coach, badge Nutrition
  autre:         '#FFAC32',  // ambre        — partagé avec PHASE.ovulation (indicateur "autre")
  sliderActive:  '#5E4DE6',  // bleu-violet  — partie active des sliders (concentration/productivité/estime)
                             //                 légèrement distincte de SPORT.primary (#5B4DE5)
};


// ─── Métriques sport ─────────────────────────────────────────
//
// Toutes les couleurs utilisées dans la page Sport : fréquence
// cardiaque, RPE, niveaux d'essoufflement, jour de repos et
// palette tournante pour les types d'activités.

export const SPORT = {
  primary:  '#5B4DE5',  // bleu-violet — couleur principale du thème Sport (nav, titres, RPE > 5)

  // ── Fréquence cardiaque ──────────────────────────────────
  fcEffort: '#8FD1D4',  // teal clair — ligne FC Effort ; aussi couleur du RPE ≤ 5 (voir rpeFacile)
  fcRepos:  '#ffbd5c',  // jaune      — ligne FC Repos

  // ── RPE (Rating of Perceived Exertion) ───────────────────
  //
  // Les deux niveaux sont désormais *nommés* dans le formulaire (« Modéré » /
  // « Intense ») : le retour bêta portait sur l'absence de libellé, pas sur les
  // teintes, qui restent inchangées.
  rpeFacile:  '#8FD1D4',  // teal clair — RPE ≤ 5 (effort facile/modéré) ; même teinte que le donut (tranche « Modéré ») = fcEffort
  rpeIntense: '#5B4DE5',  // bleu       — RPE > 5 (effort intense), même valeur que primary

  // ── Niveaux d'essoufflement (graphique area à 4 zones) ───
  soufflement: {
    repos:   '#E0E0E0',  // gris clair  — niveau 0 (repos)
    faible:  '#91fac1',  // vert clair  — niveau 1 (faible effort)
    modere:  '#8FD1D4',  // teal clair  — niveau 2 (effort modéré)
    intense: '#5b4de5',  // bleu-violet — niveau 3 (effort intense)
  },

  // ── Divers ───────────────────────────────────────────────
  jourRepos:       '#C3BEEA',  // lavande pâle — indicateur jour de repos (anneau coach) ;
                               //                aussi couleur inactive des sliders FormMental
  activitePalette: [           // 6 couleurs tournantes pour les types d'activités
    '#5B4DE5',  // bleu-violet
    '#91FAC1',  // vert clair
    '#60A5FA',  // bleu ciel
    '#6EE7B7',  // vert menthe
    '#1D4ED8',  // bleu foncé
    '#4ADE80',  // vert vif
  ],
};


// ─── Neutres d'interface ─────────────────────────────────────
//
// Couleurs structurelles non liées à un domaine fonctionnel :
// texte courant, axes de graphiques SVG, messages d'état vide,
// fonds neutres, etc. Utilisées uniquement via des props style={{}}
// dans les composants React — elles ne sont PAS injectées comme
// variables CSS (les primitifs correspondants existent déjà dans tokens.css).

export const UI = {
  // ── Texte ────────────────────────────────────────────────
  white:         '#FFFFFF',  // texte blanc sur fond coloré (Calendrier, Cycle.css)
  textPrimary:   '#1A1A2E',  // presque-noir — titres et corps de texte principal (Home)
  textSecondary: '#9CA3AF',  // gris moyen   — sous-titres, captions, labels périphériques
  textMuted:     '#846277',  // mauve-wine   — libellés secondaires dans FormMental
  textLabel:     '#555555',  // gris foncé   — labels de légende (Mental, FormShare)
  textFaint:     '#888888',  // gris          — captions de légende (Sport)
  textAxis:      '#BBBBBB',  // gris clair   — valeurs des axes SVG (Home, Mental, Sport, Calendrier)
  textEmpty:     '#AAAAAA',  // gris moyen   — messages "Aucune donnée" et états vides

  // ── Sémantique ───────────────────────────────────────────
  success: '#2D9B5F',  // vert foncé — ressentis positifs (Mental) ; miroir JS du token CSS --success

  // ── Surfaces ─────────────────────────────────────────────
  surfaceEmpty: '#DDDDDD',  // gris neutre  — fallback quand la phase d'un jour est inconnue
  restDayBg:    '#E8E8E8',  // gris très clair — fond du jour de repos (Calendrier, Coach)
  sliderTrack:  '#EEEEEE',  // gris quasi-blanc — fond de la piste du slider (FormSport)

  // ── SVG spécifique ────────────────────────────────────────
  emptyArc: 'rgba(200,200,200,0.2)',  // gris transparent — arcs SVG sans données (Coach mini-cercles)
};


// ─── Dashboard Home ───────────────────────────────────────────
//
// Couleurs propres aux éléments visuels du dashboard :
// anneaux concentriques, axe du donut SVG et icône de phase.

export const HOME = {
  emptySlice:     '#EDE8F5',               // violet très pâle — tranche "vide" des anneaux (pas de données)
  axisRing:       '#C4B8CB',               // mauve gris — labels des jours sur l'axe angulaire du donut
  iconFallbackBg: 'rgba(255,172,50,0.15)', // ambre 15 % — fond de l'icône de phase quand la phase est inconnue
                                           //               (correspond à PHASE.ovulation = #FFAC32 à faible opacité)
};


// ─── Articles / Blog ─────────────────────────────────────────
//
// Couleurs des badges de catégorie et gradients décoratifs
// des cartes d'article. Les badges Nutrition et par défaut
// réutilisent MENTAL.accent et MENTAL.concentration.

export const ARTICLE = {
  fitnessBadge: '#4DD9C0',  // teal vif — badge catégorie "Fitness" (unique à cette section)
  // nutritionBadge → MENTAL.accent       (#9B8EF5) — badge catégorie "Nutrition"
  // defaultBadge   → MENTAL.concentration (#F93E6E) — toutes autres catégories

  // 5 gradients rotatifs pour les visuels hero des cartes d'article
  cardGradients: [
    'linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)',  // violet → rose
    'linear-gradient(135deg, #a5f3fc 0%, #6ee7b7 100%)',  // cyan   → vert menthe
    'linear-gradient(135deg, #fde68a 0%, #fca5a5 100%)',  // jaune  → saumon
    'linear-gradient(135deg, #ddd6fe 0%, #fbcfe8 100%)',  // lavande → rose pâle
    'linear-gradient(135deg, #bbf7d0 0%, #bfdbfe 100%)',  // vert clair → bleu ciel
  ],
};


// ─── Camemberts du dashboard (anneau triple) ─────────────────
//
// Mappings label (envoyé par le backend) → couleur de segment.
// Modifier ici pour changer les couleurs du diagramme sans
// toucher aux composants.

export const CYCLE_PIE_COLORS = {
  'Règles':       PHASE.regles,        // rouge
  'Folliculaire': PHASE.folliculaire,  // teal
  'Ovulation':    PHASE.ovulation,     // ambre
  'Lutéale':      PHASE.luteale,       // violet
};

export const MENTAL_PIE_COLORS = {
  'Positif': MENTAL.concentration,  // rose vif
  'Négatif': MENTAL.productivite,   // rose moyen
  'Neutre':  MENTAL.estime,         // violet pâle
};

export const SPORT_PIE_COLORS = {
  'Intense': SPORT.rpeIntense,         // bleu-violet
  'Modéré':  SPORT.soufflement.modere, // teal clair
  'Repos':   SPORT.soufflement.repos,  // gris neutre
};


// ─── Coach ────────────────────────────────────────────────────
//
// Palette de pastilles couleur pour les équipes du coach (page CoachTeams).
// Réutilise des teintes déjà définies ailleurs plutôt que d'en créer de nouvelles.

export const COACH = {
  teamPalette: [
    SPORT.primary,        // bleu-violet
    PHASE.regles,         // rouge vif
    PHASE.ovulation,      // ambre
    PHASE.folliculaire,   // teal vif
    MENTAL.accent,        // violet moyen
    MENTAL.productivite,  // rose moyen
    SPORT.fcRepos,        // jaune
    PHASE.luteale,        // violet
  ],
};


// ─── Maps de compatibilité ────────────────────────────────────
//
// Utilisées dans Sport.js, Mental.js et Coach.js pour colorer
// les indicateurs de phase sur les graphiques (cercles, arcs SVG).
// Accepte aussi les clés anglaises renvoyées par certains endpoints.

export const PHASE_COLORS_MAP = {
  // Clés françaises (nomenclature interne)
  regles:       PHASE.regles,
  folliculaire: PHASE.folliculaire,
  ovulation:    PHASE.ovulation,
  luteale:      PHASE.luteale,
  // Alias anglais (certains endpoints backend)
  menstrual:    PHASE.regles,
  follicular:   PHASE.folliculaire,
  luteal:       PHASE.luteale,
};

// Configuration complète par phase : couleur principale, couleur pastel, label affiché.
// Les clés sont celles renvoyées par le moteur de cycle (app/core/cycle_engine.Phase).
export const PHASE_COLORS = {
  REGLES:       { main: PHASE.regles,       pastel: PHASE.folliculaire, label: 'Règles'       },
  FOLLICULAIRE: { main: PHASE.folliculaire, pastel: PHASE.folliculaire, label: 'Folliculaire' },
  OVULATION:    { main: PHASE.ovulation,    pastel: PHASE.luteale,      label: 'Ovulation'    },
  LUTEALE:      { main: PHASE.luteale,      pastel: PHASE.folliculaire, label: 'Lutéale'      },
};

// Normalise une chaîne quelconque vers la clé PHASE_COLORS.
// Tolère l'ancien vocabulaire (MENSTRUATIONS / OVULATOIRE) : des relevés ou des caches
// antérieurs à la refonte du moteur peuvent encore le renvoyer.
export const toPhaseKey = (str) =>
  str?.toUpperCase().replace('MENSTRUATIONS', 'REGLES').replace('OVULATOIRE', 'OVULATION') ?? null;

// Libellé français (tel que renvoyé par /dashboard.current_phase) → clé du moteur.
// Le blog rattache ses articles à des clés (`REGLES`…) alors que le dashboard expose
// des libellés : sans cette table, les deux ne se reconnaissent pas.
export const PHASE_KEY_FROM_LABEL = {
  'Règles': 'REGLES',
  'Folliculaire': 'FOLLICULAIRE',
  'Ovulation': 'OVULATION',
  'Lutéale': 'LUTEALE',
};

// Clé d'API du moteur → slug de style utilisé par les classes CSS et PHASE_COLORS_MAP.
export const PHASE_API_MAP = {
  REGLES: 'regles',
  FOLLICULAIRE: 'folliculaire',
  OVULATION: 'ovulation',
  LUTEALE: 'luteale',
  // Alias de l'ancien vocabulaire, pour la même raison que toPhaseKey.
  MENSTRUATIONS: 'regles',
  OVULATOIRE: 'ovulation',
};


// ─── Injection CSS ────────────────────────────────────────────
//
// À appeler UNE SEULE FOIS dans index.js, avant le premier rendu React.
// Écrit les valeurs JS dans les propriétés CSS custom de :root afin que
// les fichiers .css puissent utiliser var(--phase-*), var(--mental-*), etc.
//
// Les neutrals UI (grays) ne sont PAS injectés ici : ils correspondent
// à des primitifs déjà déclarés dans tokens.css (--c-gray-*, --c-ink-*).

export function injectCSSColors() {
  const r = document.documentElement;

  // Phases du cycle
  r.style.setProperty('--phase-menstruations', PHASE.regles);
  r.style.setProperty('--phase-folliculaire',  PHASE.folliculaire);
  r.style.setProperty('--phase-ovulation',     PHASE.ovulation);
  r.style.setProperty('--phase-luteale',       PHASE.luteale);

  // Couleurs primaires de chaque thème (nav active, titres de section)
  r.style.setProperty('--phase-primary',  PHASE.primary);
  r.style.setProperty('--mental-primary', MENTAL.primary);
  r.style.setProperty('--sport-primary',  SPORT.primary);

  // Sport — fréquence cardiaque (écrase les tokens orange du thème de base)
  r.style.setProperty('--sport',      SPORT.fcEffort);
  r.style.setProperty('--sport-rest', SPORT.fcRepos);

  // Sport — RPE
  r.style.setProperty('--sport-rpe-facile',  SPORT.rpeFacile);
  r.style.setProperty('--sport-rpe-intense', SPORT.rpeIntense);

  // Mental — métriques du widget graphique
  r.style.setProperty('--mental-concentration', MENTAL.concentration);
  r.style.setProperty('--mental-productivite',  MENTAL.productivite);
  r.style.setProperty('--mental-estime',        MENTAL.estime);
}
